import { useRef } from 'react'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import { DateTimePicker } from '@mui/x-date-pickers-pro'
import { useNavigate, useParams, useBlocker, useSubmit } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { getEventDetails, setEvent } from '../../../../store.slice'
import { useAppDispatch, useAppSelector } from '../../../../store'
import { useContextStep } from '../CreateEvents.Context'
import { LogisticSchema } from '../../../events/Events.schema'
import Form from '../../../root/components/Form'
import { onSubmitData } from '../CreateEvent.utils'

export default function LogisticStep3() {
  const { t } = useTranslation('events')
  const navigate = useNavigate()
  const submit = useSubmit()
  const dispatch = useAppDispatch()
  const formRef = useRef<HTMLFormElement>(null)
  const { eventId } = useParams()
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const [step, setStep] = useContextStep()
  const eventDetails = useAppSelector(state => getEventDetails(state)(eventId))

  const { control, getValues, trigger } = useForm<LogisticSchema>({
    defaultValues: {
      abstract_submission_start: eventDetails.event?.abstract_submission_start ?? null,
      abstract_submission_end: eventDetails.event?.abstract_submission_end ?? null,
      abstract_review_start: eventDetails.event?.abstract_review_start ?? null,
      abstract_review_end: eventDetails.event?.abstract_review_end ?? null,
      sponsorship_request_start: eventDetails.event?.sponsorship_request_start ?? null,
      sponsorship_request_end: eventDetails.event?.sponsorship_request_end ?? null,
    },
    resolver: zodResolver(LogisticSchema),
  })

  useBlocker(({ nextLocation }) => {
    const values = getValues()
    const data = {
      ...eventDetails.event,
      ...values,
    }
    dispatch(setEvent({ data, key: eventId }))

    if (!nextLocation.pathname.includes('/admin/events/')) {
      return !window.confirm(
        `${t(
          'Êtes-vous sûr de vouloir quitter cette page ? Des modifications non enregistrées seront perdues'
        )}.`
      )
    }
    return false
  })

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const values = getValues()
    const data = {
      ...eventDetails,
      event: {
        ...eventDetails.event,
        ...values,
      },
    }
    dispatch(setEvent({ data: data.event, key: eventId }))
    onSubmitData(data, eventId, submit)
  }

  return (
    <Form onSubmit={onSubmitForm} ref={formRef}>
      <Stack direction="column" spacing={2}>
        <h1>{t('Logistique')}</h1>
        <Divider />

        <h5>{t('Date de soumission des résumés')}</h5>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Controller
            control={control}
            name="abstract_submission_start"
            render={({ field, fieldState }) => (
              <DateTimePicker
                label={t('Date de début')}
                name={field.name}
                defaultValue={field.value ? new Date(field.value) : null}
                onChange={date => {
                  field.onChange(date?.toISOString() ?? '')
                }}
                sx={{ flex: '1 0 auto' }}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                    variant: 'filled',
                  },
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="abstract_submission_end"
            render={({ field, fieldState }) => (
              <DateTimePicker
                label={t('Date de fin')}
                name={field.name}
                defaultValue={field.value ? new Date(field.value) : null}
                onChange={date => {
                  field.onChange(date?.toISOString() ?? '')
                }}
                sx={{ flex: '1 0 auto' }}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                    variant: 'filled',
                  },
                }}
              />
            )}
          />
        </Stack>

        <h5>{t('Date de revue des résumés')}</h5>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Controller
            control={control}
            name="abstract_review_start"
            render={({ field, fieldState }) => (
              <DateTimePicker
                label={t('Date de début')}
                name={field.name}
                defaultValue={field.value ? new Date(field.value) : null}
                onChange={date => {
                  field.onChange(date?.toISOString() ?? '')
                }}
                sx={{ flex: '1 0 auto' }}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                    variant: 'filled',
                  },
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="abstract_review_end"
            render={({ field, fieldState }) => (
              <DateTimePicker
                label={t('Date de fin')}
                name={field.name}
                defaultValue={field.value ? new Date(field.value) : null}
                onChange={date => {
                  field.onChange(date?.toISOString() ?? '')
                }}
                sx={{ flex: '1 0 auto' }}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                    variant: 'filled',
                  },
                }}
              />
            )}
          />
        </Stack>

        <h5>{t('Date de demande des prises en charge (Congrès seulement)')}</h5>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Controller
            control={control}
            name="sponsorship_request_start"
            render={({ field, fieldState }) => (
              <DateTimePicker
                label={t('Date de début')}
                name={field.name}
                defaultValue={field.value ? new Date(field.value) : null}
                onChange={date => {
                  field.onChange(date?.toISOString() ?? '')
                }}
                sx={{ flex: '1 0 auto' }}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                    variant: 'filled',
                  },
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="sponsorship_request_end"
            render={({ field, fieldState }) => (
              <DateTimePicker
                label={t('Date de fin')}
                name={field.name}
                defaultValue={field.value ? new Date(field.value) : null}
                onChange={date => {
                  field.onChange(date?.toISOString() ?? '')
                }}
                sx={{ flex: '1 0 auto' }}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                    variant: 'filled',
                  },
                }}
              />
            )}
          />
        </Stack>

        <Stack sx={{ minHeight: '3.5rem' }} />
        <Stack
          direction="row"
          padding="0 2rem 0.5rem"
          sx={{
            position: 'fixed',
            zIndex: 1,
            width: '100%',
            bottom: { xs: '7rem', md: '3rem', lg: '2.5rem' },
            left: 0,
            pointerEvents: 'none',
          }}
        >
          <Stack minWidth={{ xs: '0', md: '340px', lg: '400px' }} />
          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 2, md: 3 }}
            justifyContent="space-between"
            padding="0 1rem"
            flex="2 0 auto"
          >
            <Button
              onClick={event => {
                event.preventDefault()
                if (window.confirm(t('Êtes-vous sûr de vouloir annuler ?'))) navigate('/dashboard')
              }}
              sx={{ pointerEvents: 'all' }}
            >
              {t('Annuler')}
            </Button>
            <Stack direction="row" spacing={{ xs: 1, sm: 2, md: 3 }}>
              <Button
                variant="contained"
                onClick={() => {
                  navigate(`/admin/events/${eventId}/sessions`)
                }}
                sx={{ pointerEvents: 'all' }}
              >
                {t('Précédent')}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (step < 4) {
                    setStep(4)
                  }
                  navigate(`/admin/events/${eventId}/conditions`)
                }}
                sx={{ pointerEvents: 'all' }}
              >
                {t('Suivant')}
              </Button>
              <Button
                disabled={step < 5}
                variant="contained"
                type="submit"
                sx={{ pointerEvents: 'all' }}
              >
                {t('Valider')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Form>
  )
}
